import { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApp } from 'firebase/app';
import { getAuth, getIdToken } from 'firebase/auth';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

const getSymfonyApiUrl = (): string => {
  if (process.env.API_SYMFONY_URL) {
    return process.env.API_SYMFONY_URL;
  }

  if (!process.env.API_SYMFONY_PORT) {
    throw new Error(
      'API_SYMFONY_URL (or API_SYMFONY_PORT) is not set for Symfony API.',
    );
  }

  const host = process.env.API_SYMFONY_HOST || 'localhost';
  return `http://${host}:${process.env.API_SYMFONY_PORT}`;
};

const instance: AxiosInstance = axios.create({
  baseURL: getSymfonyApiUrl(),
  withCredentials: true,
});

instance.interceptors.request.use(
  async (config) => {
    const currentUser = getAuth(getApp()).currentUser;
    const token = currentUser ? await getIdToken(currentUser) : null;

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const symfonyBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' },
  ): BaseQueryFn<{
    url: string;
    method?: AxiosRequestConfig['method'];
    data?: AxiosRequestConfig['data'];
    params?: AxiosRequestConfig['params'];
    headers?: AxiosRequestConfig['headers'];
  }> =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await instance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        return {
          error: {
            status: error.response?.status,
            data: error.response?.data || error.message,
          },
        };
      }

      return {
        error: {
          status: 500,
          data: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  };

export default instance;
